import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import OrentisLogo from "./OrentisLogo";
import LeadModal from "./LeadModal";

const LINKS = [
  { label: "Index", href: "#hero", no: "00" },
  { label: "Manifesto", href: "#about", no: "01" },
  { label: "Services", href: "#services", no: "02" },
  { label: "Clients", href: "#clients", no: "03" },
  { label: "Contact", href: "#contact", no: "04" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
        className="fixed top-0 left-0 right-0 z-50"
        data-testid="site-nav"
      >
        <div
          className={`transition-[background,backdrop-filter,border-color] duration-500 ${
            scrolled
              ? "bg-[#0e0e0e]/70 backdrop-blur-xl border-b border-white/10"
              : "bg-transparent border-b border-transparent"
          }`}
        >
          <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-[72px] flex items-center justify-between">
            <a href="#hero" className="flex items-center gap-3 group" data-testid="brand-logo">
              <OrentisLogo
                responsive={{ sm: "md", md: "lg", lg: "lg" }}
                showTag
              />
            </a>

            <nav className="hidden md:flex items-center gap-9">
              {LINKS.slice(1).map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="text-[12px] tracking-[0.22em] uppercase text-[var(--ink-dim)] hover:text-[var(--ink)] transition-colors duration-300 flex items-center gap-2 group"
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                >
                  <span className="font-mono text-[10px] text-[var(--gold)] opacity-70 group-hover:opacity-100">
                    {l.no}
                  </span>
                  <span className="link-underline">{l.label}</span>
                </a>
              ))}
            </nav>

            <button
              type="button"
              onClick={() => setLeadOpen(true)}
              className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[var(--bg)] bg-[var(--gold)] hover:bg-[var(--gold-soft)] transition-colors duration-300 px-5 py-3 rounded-full"
              data-testid="nav-cta-talk"
            >
              Let’s Talk
              <span aria-hidden>↗</span>
            </button>

            <button
              onClick={() => setOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center border border-white/15 rounded-full"
              aria-label="Open menu"
              data-testid="nav-menu-open"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-[#0e0e0e]/95 backdrop-blur-2xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="p-6 flex items-center justify-between">
              <OrentisLogo size="sm" showTag />
              <button
                onClick={() => setOpen(false)}
                className="w-10 h-10 flex items-center justify-center border border-white/15 rounded-full"
                aria-label="Close menu"
                data-testid="nav-menu-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 pt-6 flex flex-col gap-6">
              {LINKS.slice(1).map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.12 + i * 0.08, duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
                  className="font-display text-5xl leading-none flex items-baseline gap-3"
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                >
                  <span className="font-mono text-xs text-[var(--gold)]">{l.no}</span>
                  {l.label}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LeadModal open={leadOpen} onOpenChange={setLeadOpen} />
    </>
  );
}
